import React, { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  GitHub as GitHubIcon,
  Language as WebsiteIcon,
  FormatQuote as QuoteIcon,
} from '@mui/icons-material';
import StarRating from './StarRating';
import { Review } from '../api/reviewsApi';
import brand from '../../../config/brand';

interface ReviewCardProps {
  review: Review;
}

const MAX_TEXT_LENGTH = 200;

const ReviewCard: React.FC<ReviewCardProps> = ({ review }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const isLong = review.text.length > MAX_TEXT_LENGTH;
  const displayText =
    isLong && !expanded
      ? review.text.slice(0, MAX_TEXT_LENGTH) + '...'
      : review.text;

  const socialIcons = [
    { key: 'linkedin', icon: <LinkedInIcon />, label: 'LinkedIn' },
    { key: 'twitter', icon: <TwitterIcon />, label: 'Twitter' },
    { key: 'facebook', icon: <FacebookIcon />, label: 'Facebook' },
    { key: 'instagram', icon: <InstagramIcon />, label: 'Instagram' },
    { key: 'github', icon: <GitHubIcon />, label: 'GitHub' },
    { key: 'website', icon: <WebsiteIcon />, label: 'Website' },
  ];

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    return parts
      .slice(0, 2)
      .map((p) => p.charAt(0))
      .join('')
      .toUpperCase();
  };

  return (
    <Card
      sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'visible',
        background:
          theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
            : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid',
        borderColor:
          theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(0,0,0,0.06)',
        borderRadius: 3,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow:
            theme.palette.mode === 'dark'
              ? '0 20px 60px rgba(0,0,0,0.4)'
              : '0 20px 60px rgba(0,0,0,0.12)',
          borderColor: `rgba(${brand.colorsRgb.fire}, 0.3)`,
        },
      }}
    >
      {/* Decorative quote mark */}
      <QuoteIcon
        sx={{
          position: 'absolute',
          top: -12,
          left: 16,
          fontSize: 40,
          color: brand.colors.fire,
          opacity: 0.6,
          transform: 'rotate(180deg)',
        }}
      />

      {/* User info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, mt: 1 }}>
        <Avatar
          src={review.userAvatar}
          sx={{
            width: 48,
            height: 48,
            bgcolor: brand.colors.fire,
            fontWeight: 700,
            fontSize: '1rem',
          }}
        >
          {getInitials(review.userName)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {review.userName}
          </Typography>
          <StarRating
            value={review.rating}
            readOnly
            size="small"
            showValue={false}
          />
        </Box>
      </Box>

      {/* Review text */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          flex: 1,
          lineHeight: 1.7,
          fontStyle: 'italic',
          mb: 2,
        }}
      >
        &ldquo;{displayText}&rdquo;
        {isLong && (
          <Typography
            component="span"
            variant="body2"
            sx={{
              color: brand.colors.fire,
              cursor: 'pointer',
              fontWeight: 600,
              ml: 0.5,
              fontStyle: 'normal',
              '&:hover': { textDecoration: 'underline' },
            }}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Show less' : 'Read more'}
          </Typography>
        )}
      </Typography>

      {/* Social links */}
      {review.socialLinks && (
        <Box sx={{ display: 'flex', gap: 0.5, mt: 'auto' }}>
          {socialIcons
            .filter(
              (s) =>
                review.socialLinks?.[
                  s.key as keyof typeof review.socialLinks
                ]
            )
            .map((s) => (
              <Tooltip key={s.key} title={s.label}>
                <IconButton
                  size="small"
                  component="a"
                  href={
                    review.socialLinks?.[
                      s.key as keyof typeof review.socialLinks
                    ] || '#'
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'text.secondary',
                    '&:hover': { color: brand.colors.fire },
                  }}
                >
                  {s.icon}
                </IconButton>
              </Tooltip>
            ))}
        </Box>
      )}
    </Card>
  );
};

export default ReviewCard;
